import { memo } from "react";
import { ContextSelectedOptions } from "../../../../../context-selector/src/ContextDropdown";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { useSelectedRowsSortedTopTenData } from "../../../components/topTenDataTable/hooks/useSelectedRowsSortedTopTenData";
import { TopTenDataTables } from "../../../components/topTenDataTable/TopTenDataTables";
import { ReviewDataResponse } from "../hooks/useFetchReviewData";
import {
  TopTenResponse,
  useFetchTopTenPurchase,
  useFetchTopTenSales
} from "../hooks/useFetchTopTen";

interface TopTenTabProps {
  /**
   * Currently applied filters
   */
  appliedFilters: {
    contextOptions: ContextSelectedOptions;
    temporalOptions: TemporalContext;
  };
  /**
   * Indicates if the user has applied any filters
   */
  hasUserApplied: boolean;
  /**
   * Data from the main review data table, if any rows are selected
   */
  reviewData: ReviewDataResponse;
  /**
   * Loading state for the review data fetch
   */
  isReviewDataLoading: boolean;
  /**
   * Optional array of user-selected rows from either purchase or sales grid
   */
  selectedRowData: TopTenResponse[];
}

export const TopTenTabContent = memo(function TopTenTabContent(
  props: TopTenTabProps
) {
  const { data: topTenPurchaseData, isFetching: isTopTenPurchaseLoading } =
    useFetchTopTenPurchase(
      props.appliedFilters.contextOptions,
      props.appliedFilters.temporalOptions,
      props.hasUserApplied &&
        (!props.selectedRowData || props.selectedRowData.length === 0)
    );

  const { data: topTenSalesData, isFetching: isTopTenSalesLoading } =
    useFetchTopTenSales(
      props.appliedFilters.contextOptions,
      props.appliedFilters.temporalOptions,
      props.hasUserApplied &&
        (!props.selectedRowData || props.selectedRowData.length === 0)
    );

  // Transform selected data into ranked purchase/sales lists
  const { purchaseRowData, salesRowData } = useSelectedRowsSortedTopTenData(
    props.selectedRowData
  );

  const isLoading =
    props.isReviewDataLoading ||
    isTopTenSalesLoading ||
    isTopTenPurchaseLoading;

  // Use selected data if available, otherwise use API data
  const finalPurchaseData =
    props.selectedRowData && props.selectedRowData.length > 0
      ? purchaseRowData
      : topTenPurchaseData || [];
  const finalSalesData =
    props.selectedRowData && props.selectedRowData.length > 0
      ? salesRowData
      : topTenSalesData || [];

  return (
    <TopTenDataTables
      purchaseRowData={finalPurchaseData}
      salesRowData={finalSalesData}
      isLoading={isLoading}
    />
  );
});
